import { useEffect, useRef, useState, useCallback } from "react"
import { AnimatePresence, motion } from "motion/react"
import { PurchasProvider, usePurchase } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import { useAppState } from "../../context/appStateContext"
import useLastExpiredWasUltimate from "../../hooks/useLastExpiredWasUltimate"
import useQueryParams from "../../hooks/useQueryParams"
import useHasPro from "../../hooks/useHasPro"
import native from "../../native/native"
import StepOne from "./StepOne/StepOne"
import StepThree from "./StepThree/StepThree"
import FixedCloseButton from "../../components/FixedCloseButton/FixedCloseButton"
import LifetimeComingSoonPopup from "../BubbleExpirationPaywall/LifetimeComingSoonPopup/LifetimeComingSoonPopup"
import LifetimeBottomSheet from "../BubbleExpirationPaywall/LifetimeBottomSheet/LifetimeBottomSheet"
import styles from "./ThreeStepPaywall.module.scss"

const ThreeStepPaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <ThreeStepContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

const ThreeStepContent = () => {
  const [step, setStep] = useState(1)
  const [showLifetimePopup, setShowLifetimePopup] = useState(false)
  const [showLifetimeSheet, setShowLifetimeSheet] = useState(false)
  const [showCloseButton, setShowCloseButton] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { state: appState, actions } = useAppState()
  const purchaseActions = usePurchase().actions
  const lastExpiredSubscriptionWasUltimate = useLastExpiredWasUltimate()
  const hasPro = useHasPro()
  const { showLifetimeSecondStep } = useQueryParams()

  useEffect(() => {
    if (hasPro && step === 3) {
      setShowCloseButton(false)
      const timer = setTimeout(() => setShowCloseButton(true), 3000)
      return () => clearTimeout(timer)
    }
  }, [hasPro, step])

  useEffect(() => {
    const isDark = document.documentElement.dataset.theme === "dark"
    const bgColor = isDark ? "#111419" : "#ffffff"
    actions.setHeaderInsetColor(bgColor)
    actions.setIgnoreFooterInset(true)

    // Override SafeAreaInsetPage and root backgrounds
    // Walk up from our container to body, overriding backgrounds
    let el = containerRef.current?.parentElement
    while (el && el !== document.body) {
      el.style.backgroundColor = bgColor
      el = el.parentElement
    }
    // Also override body and html background
    document.body.style.backgroundColor = bgColor
    document.documentElement.style.backgroundColor = bgColor

    if (lastExpiredSubscriptionWasUltimate) {
      purchaseActions.setUltimateSelected(true)
    }
  }, [actions])

  const goToStep3 = useCallback(() => setStep(3), [])
  const goToStep1 = useCallback(() => setStep(1), [])

  const handleLifetimeClick = useCallback(() => {
    native.postNativeEvent("analyitcs", { name: "html_lifetime_click" })
    if (showLifetimeSecondStep) {
      setShowLifetimeSheet(true)
    } else {
      setShowLifetimePopup(true)
    }
  }, [showLifetimeSecondStep])

  const topInset = appState.initialData?.topInset ?? 0

  return (
    <div ref={containerRef} className={styles.container}>
      {step === 3 && showCloseButton && (
        <FixedCloseButton
          topInset={topInset + 12}
          onClick={() => native.postNativeEvent("close")}
        />
      )}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.25 }}
            className={styles.stepWrapper}
          >
            <StepOne onSkipToPlans={goToStep3} />
          </motion.div>
        )}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ duration: 0.25 }}
            className={styles.stepWrapper}
          >
            <StepThree
              onBack={goToStep1}
              onLifetimeClick={handleLifetimeClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {(showLifetimePopup || showLifetimeSheet) && (
        <div className={styles.lifetimeOverlay}>
          <LifetimeComingSoonPopup
            visible={showLifetimePopup}
            onClose={() => setShowLifetimePopup(false)}
          />
          <LifetimeBottomSheet
            visible={showLifetimeSheet}
            onClose={() => setShowLifetimeSheet(false)}
            onCtaClick={() => {
              native.logMixpanelEvent("html_lifetime_sheet_cta")
              setShowLifetimePopup(true)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default ThreeStepPaywall
